import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Text, Input, Password, Button, Link, useTheme } from 'hoi-poi-ui';
import { PasswordPolicies } from '@web/web5';
import { logEvent } from '../../tracking';
import { initSignup } from '../../services';
import { parsePolicies } from '../../utils';

export const REG_EX_EMAIL =
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g;

const SignupStart = memo(({ t, onStep, active }) => {
    const [loading, setLoading] = useState(false);
    const policies = useRef(
        parsePolicies({
            digitsMinNumber: '1',
            lowerCaseLettersMinNumber: '1',
            maxLength: '30',
            minLength: '10',
            regexDigits: '^(?=(.*\\d){1})',
            regexLowerCase: '^(?=(.*[a-z]){1})',
            regexSpecial:
                '^(?=(.*[ !@#$%^\u0026*()_\\+\\-=\\[\\]{};\':"\\|,.\u003c\u003e\\/?]){1})',
            regexUpperCase: '^(?=(.*[A-Z]){1})',
            requireDigits: 'True',
            requireLowerCaseLetters: 'True',
            requireSpecialLetters: 'True',
            uppercaseLettersMin: 'True',
            specialLettersMinNumber: '1',
            upperCaseLettersMinNumber: '1',
        }),
    );
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState();

    const theme = useTheme();

    const onChange = (field) => (value) => {
        if (errors) setErrors({ ...errors, [field]: null });
        setForm({ ...form, [field]: value });
    };

    const onContinue = useCallback(() => {
        let newErrors = {};

        // Requireds
        if (!form.name) newErrors.name = t('label_field_required');
        if (!form.email) newErrors.email = t('label_field_required');
        if (!form.password) newErrors.password = t('label_field_required');

        // Error validation
        if (form.email && !form.email.trim().match(REG_EX_EMAIL))
            newErrors.email = t('error_validation_email');

        if (
            !newErrors.password &&
            !policies.current.every((policy) => policy.condition(form.password))
        ) {
            newErrors.password = t('error_password_policies');
        }

        if (Object.keys(newErrors)?.length) setErrors(newErrors);
        else {
            setLoading(true);
            initSignup({ email: form.email })
                .then(({ data }) => {
                    form.token = data.token;
                    onStep(1, form);
                })
                .catch((e) => {
                    console.error(e);
                    onStep(4);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [form, onStep, t]);

    useEffect(() => {
        if (!active) return;
        logEvent({ event: 'signup', functionality: 'openedWeb' });
    }, [active]);

    useEffect(() => {
        let searchParams = new URLSearchParams(window.location.search);
        if (searchParams && searchParams.has('email')) {
            setForm({ email: decodeURIComponent(searchParams.get('email')) });
        }
    }, []);

    return (
        <div className="signup__start">
            <Text type="h4" color={theme.colors.actionMajor[500]}>
                {t('label_signup_start_title')}
            </Text>
            <form>
                <Input
                    label={t('label_signup_fullname')}
                    placeholder={t('label_signup_fullname_placeholder')}
                    onChange={onChange('name')}
                    onEnter={onContinue}
                    value={form.name}
                    type="text"
                    isRequired
                    isFullWidth
                    autoFocus={true}
                    error={errors?.name}
                />
                <Input
                    label={t('label_signup_email')}
                    placeholder={t('you@example.com')}
                    onChange={onChange('email')}
                    onEnter={onContinue}
                    value={form.email}
                    type="email"
                    isRequired
                    isFullWidth
                    error={errors?.email}
                />
                <Password
                    label={t('label_signup_password')}
                    placeholder={t('label_signup_password_placeholder')}
                    onChange={onChange('password')}
                    onEnter={onContinue}
                    value={form.password}
                    isRequired
                    isFullWidth
                    error={errors?.password}
                />
                <PasswordPolicies
                    className="signup__password-policies"
                    password={form.password}
                    policies={policies.current}
                    t={t}
                />
            </form>
            <footer>
                <Button size="big" onClick={onContinue} isLoading={loading}>
                    {t('action_next')}
                </Button>
            </footer>
        </div>
    );
});

export default SignupStart;
